/** Shared stakeholder coverage grid keys (Account Details + Coverage Matrix modal). */

import {
  buildOverviewVendorIdSets,
  isAccountTeamStakeholder,
  pairMatchesStakeholder,
} from "./relationshipUtils";

export function buildAllowedStakeholderKeys(
  accountTeam,
  sponsors,
  customerTeam
) {
  const allowed = new Set();
  for (const customer of customerTeam || []) {
    const customerId = String(customer.id);
    for (const member of accountTeam || []) {
      allowed.add(`account_team_member:${member.id}:${customerId}`);
    }
    for (const sponsor of sponsors || []) {
      // Extra sponsors use account_sponsor only — do not also allow executive:…
      // for the same person, or uncheck leaves a twin key that re-saves on POST.
      const vendorType =
        sponsor.type === "additional" ? "account_sponsor" : "executive";
      const vendorId = String(
        vendorType === "account_sponsor"
          ? sponsor.id
          : sponsor.executive_id || sponsor.id
      );
      allowed.add(`${vendorType}:${vendorId}:${customerId}`);
    }
  }
  return allowed;
}

/** Keys that represent the same vendor↔contact pair (extra-sponsor twins). */
export function coverageLinkTwinKeys(
  vendorType,
  vendorId,
  customerId,
  sponsors = []
) {
  const customer = String(customerId);
  const vendor = String(vendorId);
  const keys = new Set([`${vendorType}:${vendor}:${customer}`]);

  if (vendorType === "account_sponsor") {
    const sponsor = (sponsors || []).find(sp => String(sp.id) === vendor);
    const execId = sponsor?.executive_id || sponsor?.id;
    if (execId) keys.add(`executive:${execId}:${customer}`);
  } else if (vendorType === "executive") {
    const additional = (sponsors || []).find(
      sp =>
        sp.type === "additional" && String(sp.executive_id || sp.id) === vendor
    );
    if (additional) keys.add(`account_sponsor:${additional.id}:${customer}`);
  }

  return [...keys];
}

/** Toggle a coverage cell and clear/set any extra-sponsor twin keys together. */
export function toggleCoverageLinkMap(
  linkMap,
  vendorType,
  vendorId,
  customerId,
  sponsors = []
) {
  const primaryKey = `${vendorType}:${vendorId}:${customerId}`;
  const turningOn = !linkMap?.[primaryKey];
  const next = { ...(linkMap || {}) };
  for (const key of coverageLinkTwinKeys(
    vendorType,
    vendorId,
    customerId,
    sponsors
  )) {
    if (turningOn) next[key] = true;
    else delete next[key];
  }
  return next;
}

/** Build preview PairKey inputs from the same API links used on save. */
export function apiLinksToPairKeys(links) {
  const keys = [];
  const seen = new Set();

  for (const link of links || []) {
    const subjectId = String(
      link.account_team_member || link.executive_team_member || ""
    ).trim();
    const contactId = String(link.customer_team_member || "").trim();
    if (!subjectId || !contactId) continue;

    const subjectType = String(link.team_type || "account_team_member").trim();
    // Dedupe on vendor↔contact so twin keys cannot double-count.
    const identity = `${subjectId}::${contactId}`;
    if (seen.has(identity)) continue;
    seen.add(identity);
    keys.push({
      subject_type: subjectType,
      subject_id: subjectId,
      contact_id: contactId,
    });
  }

  return keys;
}

/** Build save payload from checked keys; one API link per vendor↔contact pair. */
export function coverageLinkMapToApiLinks(linkMap, allowedKeys, sponsors = []) {
  const links = [];
  const seen = new Set();

  for (const key of allowedKeys || []) {
    if (!linkMap?.[key]) continue;
    const { vendorType, vendorId, customerId } = parseStakeholderLinkKey(key);
    const link = coverageLinkKeyToApiInput(
      vendorType,
      vendorId,
      customerId,
      sponsors
    );
    const pairKey = stakeholderApiLinkPairKey(link);
    if (!pairKey || seen.has(pairKey)) continue;
    seen.add(pairKey);
    links.push(link);
  }

  return links;
}

export function filterStakeholderLinkMap(linkMap, allowedKeys) {
  const next = {};
  for (const [key, value] of Object.entries(linkMap || {})) {
    if (value && allowedKeys.has(key)) next[key] = true;
  }
  return next;
}

export function linksMapFromRelationships(relationships, teamContext) {
  const map = {};
  for (const rel of relationships || []) {
    if (!rel?.subject_type || !rel?.subject_id || !rel?.object_id) continue;
    map[`${rel.subject_type}:${rel.subject_id}:${rel.object_id}`] = true;
  }
  if (!teamContext) return map;
  const allowed = buildAllowedStakeholderKeys(
    teamContext.accountTeam,
    teamContext.sponsors,
    teamContext.customerTeam
  );
  return filterStakeholderLinkMap(map, allowed);
}

export function parseStakeholderLinkKey(key) {
  const [vendorType, vendorId, customerId] = String(key).split(":");
  return { vendorType, vendorId, customerId };
}

/** Matrix cell → stakeholder-relationships API link (customerId = legacy CTM id). */
export function buildStakeholderLinkFromMatrixCell(stakeholder, contact) {
  if (!stakeholder || !contact) return null;
  const customerId = String(
    contact.raw?.legacy_customer_team_member_id ||
      contact.raw?.legacyCustomerTeamMemberId ||
      contact.id ||
      contact.user_id ||
      ""
  );
  if (!customerId) return null;

  if (isAccountTeamStakeholder(stakeholder)) {
    return {
      vendorType: "account_team_member",
      vendorId: String(stakeholder.sourceId),
      customerId,
    };
  }

  return {
    vendorType: "executive",
    vendorId: String(stakeholder.sourceId),
    customerId,
  };
}

export function buildCoverageCellKey(
  vendorType,
  vendorId,
  customerTeamMemberId
) {
  return `${vendorType}:${vendorId}:${customerTeamMemberId}`;
}

export function unwrapOverviewContent(response) {
  return response?.content ?? response ?? {};
}

/** Build POST payload links from relationship-assessment overview `mapped_pairs`. */
export function overviewMappedPairsToApiLinks(overview) {
  const { accountTeamIds } = buildOverviewVendorIdSets(overview);

  return (overview?.mapped_pairs || [])
    .filter(pair => pair.is_active !== false)
    .map(pair => {
      const userId = String(pair.user_id);
      const contactId = String(pair.contact_id);
      const base = {
        customer_team_member: contactId,
        relationship_percentage: null,
        is_priority: true,
      };

      if (accountTeamIds.has(userId)) {
        return {
          ...base,
          team_type: "account_team_member",
          account_team_member: userId,
        };
      }

      return {
        ...base,
        team_type: "executive_team_member",
        executive_team_member: userId,
      };
    });
}

/** Build POST payload links from matrix pairs already loaded in the UI (no overview GET). */
export function matrixPairsToApiLinks(pairs, stakeholders, contacts) {
  const contactById = new Map(
    (contacts || []).map(contact => [String(contact.id), contact])
  );
  const links = [];
  const seen = new Set();

  for (const pair of pairs || []) {
    const contact = contactById.get(String(pair.contactId));
    const stakeholder = (stakeholders || []).find(item =>
      pairMatchesStakeholder(pair, item.id)
    );
    if (!contact || !stakeholder) continue;

    const link = buildCustomerStakeholderApiLink(stakeholder, contact);
    if (!link) continue;

    const key = stakeholderApiLinkPairKey(link);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    links.push(link);
  }

  return links;
}

/** Coverage grid link map from overview `mapped_pairs` (no customers GET). */
export function linksMapFromOverviewMappedPairs(overview, teamContext) {
  const { accountTeamIds } = buildOverviewVendorIdSets(overview);
  const sponsors = teamContext?.sponsors || [];
  const map = {};

  for (const pair of overview?.mapped_pairs || []) {
    if (pair.is_active === false) continue;

    const userId = String(pair.user_id);
    const contactId = String(pair.contact_id);

    if (accountTeamIds.has(userId)) {
      map[`account_team_member:${userId}:${contactId}`] = true;
      continue;
    }

    const additional = sponsors.find(
      sp => sp.type === "additional" && String(sp.executive_id || "") === userId
    );
    if (additional) {
      map[`account_sponsor:${additional.id}:${contactId}`] = true;
      continue;
    }
    map[`executive:${userId}:${contactId}`] = true;
  }

  if (!teamContext) return map;

  const allowed = buildAllowedStakeholderKeys(
    teamContext.accountTeam,
    teamContext.sponsors,
    teamContext.customerTeam
  );
  return filterStakeholderLinkMap(map, allowed);
}

export function extractStakeholderRelationshipsList(response) {
  if (Array.isArray(response?.content)) return response.content;
  if (Array.isArray(response)) return response;
  return [];
}

export function relationshipToApiLinkInput(item) {
  if (!item || item.active === false) return null;

  const customerId = String(item.customer_team_member || item.object_id || "");
  if (!customerId) return null;

  const teamType = item.team_type || item.subject_type || "account_team_member";
  const vendorId = String(
    item.account_team_member ||
      item.executive_team_member ||
      item.subject_id ||
      ""
  );
  if (!vendorId) return null;

  const base = {
    customer_team_member: customerId,
    relationship_percentage: item.relationship_percentage ?? null,
    is_priority: item.is_priority ?? false,
  };

  if (teamType === "account_team_member") {
    return {
      ...base,
      team_type: "account_team_member",
      account_team_member: vendorId,
    };
  }

  return {
    ...base,
    team_type: "executive_team_member",
    executive_team_member: vendorId,
  };
}

export function buildCustomerStakeholderApiLink(stakeholder, contact) {
  const customerId = String(
    contact.raw?.legacy_customer_team_member_id ||
      contact.raw?.legacyCustomerTeamMemberId ||
      contact.id ||
      contact.user_id ||
      ""
  );
  const vendorId = String(stakeholder?.sourceId || "");
  if (!customerId || !vendorId) return null;

  if (isAccountTeamStakeholder(stakeholder)) {
    return {
      team_type: "account_team_member",
      account_team_member: vendorId,
      customer_team_member: customerId,
      relationship_percentage: null,
      is_priority: true,
    };
  }

  return {
    team_type: "executive_team_member",
    executive_team_member: vendorId,
    customer_team_member: customerId,
    relationship_percentage: null,
    is_priority: true,
  };
}

export function stakeholderApiLinkPairKey(link) {
  const vendorId = link?.account_team_member || link?.executive_team_member;
  if (!vendorId || !link?.customer_team_member) return "";
  return `${vendorId}::${link.customer_team_member}`;
}

export function coverageLinkKeyToApiInput(
  vendorType,
  vendorId,
  customerId,
  sponsors = []
) {
  const base = {
    customer_team_member: String(customerId),
    relationship_percentage: null,
    is_priority: true,
  };

  if (vendorType === "account_team_member") {
    return {
      ...base,
      team_type: "account_team_member",
      account_team_member: String(vendorId),
    };
  }

  let execId = vendorId;
  if (vendorType === "account_sponsor") {
    const sponsor = (sponsors || []).find(
      sp => String(sp.id) === String(vendorId)
    );
    execId = sponsor?.executive_id || sponsor?.id || vendorId;
  }

  return {
    ...base,
    team_type: "executive_team_member",
    executive_team_member: String(execId),
  };
}

export function relationshipItemToCoverageLinkKey(item, sponsors = []) {
  if (!item || item.active === false) return null;

  if (item.subject_type && item.subject_id && item.object_id) {
    return `${item.subject_type}:${item.subject_id}:${item.object_id}`;
  }

  const customerId = String(item.customer_team_member || "");
  if (!customerId) return null;

  const teamType = item.team_type || "account_team_member";
  const vendorId = String(
    item.account_team_member || item.executive_team_member || ""
  );
  if (!vendorId) return null;

  if (teamType === "account_team_member") {
    return `account_team_member:${vendorId}:${customerId}`;
  }

  const additional = (sponsors || []).find(
    sp => sp.type === "additional" && String(sp.executive_id || "") === vendorId
  );
  if (additional) {
    return `account_sponsor:${additional.id}:${customerId}`;
  }

  return `executive:${vendorId}:${customerId}`;
}

export function linksMapFromCustomerStakeholderRelationships(
  relationships,
  teamContext
) {
  const map = {};
  const sponsors = teamContext?.sponsors || [];
  for (const rel of relationships || []) {
    const key = relationshipItemToCoverageLinkKey(rel, sponsors);
    if (key) map[key] = true;
  }
  if (!teamContext) return map;
  const allowed = buildAllowedStakeholderKeys(
    teamContext.accountTeam,
    teamContext.sponsors,
    teamContext.customerTeam
  );
  return filterStakeholderLinkMap(map, allowed);
}

/** Map matrix link → coverage grid row (additional sponsors use account_sponsor row id). */
export function resolveCoverageHighlightCell(highlightCell, sponsors = []) {
  if (!highlightCell) return null;
  const customerTeamMemberId = String(
    highlightCell.customerTeamMemberId || highlightCell.customerId || ""
  );
  const vendorType = highlightCell.vendorType;
  const vendorId = String(highlightCell.vendorId || "");

  if (vendorType === "executive") {
    const additional = (sponsors || []).find(
      sp =>
        sp.type === "additional" && String(sp.executive_id || "") === vendorId
    );
    if (additional) {
      return {
        vendorType: "account_sponsor",
        vendorId: String(additional.id),
        customerTeamMemberId,
      };
    }
  }

  return { vendorType, vendorId, customerTeamMemberId };
}
